import { useNavigate } from 'react-router-dom'
import useApiQuery from '../../../hooks/useApiQuery'
import { fetchInfraDashboard } from '../../../lib/api'
import { PageHeader, StatCard, DataTable, StatusBadge, Card, Skeleton } from '../../../components/ui'

export default function InfraAdminDashboard() {
  const navigate = useNavigate()
  const { data, loading } = useApiQuery(() => fetchInfraDashboard())

  const stats = data?.stats || {}
  const recentTickets = data?.recent_tickets || []
  const recentRuns    = data?.recent_runs    || []
  const healthAlerts  = data?.health_alerts  || []

  return (
    <div>
      <PageHeader
        title="Infrastructure Dashboard"
        subtitle="Overview of all environments, maintenance and support"
      />

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard label="Active Clients"    value={loading ? '—' : stats.clients    ?? 0} icon="👥" color="#1A6AF5" loading={loading} />
        <StatCard label="Environments"      value={loading ? '—' : stats.environments ?? 0} icon="🖥" color="#00C8E8" loading={loading} />
        <StatCard label="Open Tickets"      value={loading ? '—' : stats.open_tickets ?? 0} icon="🎫" color="#F59E0B" loading={loading} />
        <StatCard label="Maintenance Runs"  value={loading ? '—' : stats.runs_today  ?? 0} icon="🔧" color="#10B981" loading={loading} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Health Alerts */}
        <Card className="lg:col-span-1">
          <h3 className="font-display font-bold text-sm text-white mb-4">🚨 Health Alerts</h3>
          {loading ? <Skeleton lines={4} /> : healthAlerts.length === 0 ? (
            <p className="text-xs text-[var(--text-dim)] py-4 text-center">All environments healthy ✓</p>
          ) : (
            <div className="space-y-2">
              {healthAlerts.map((a, i) => (
                <div key={i} className="flex items-center gap-3 p-2.5 rounded-lg cursor-pointer hover:bg-white/4 transition-colors"
                  onClick={() => navigate(`/infra/admin/environments/${a.environment_id}`)}>
                  <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: a.level === 'critical' ? '#ef4444' : '#f59e0b' }} />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-white truncate">{a.environment_name}</p>
                    <p className="text-[11px] text-[var(--text-dim)] truncate">{a.message}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Recent Tickets */}
        <Card className="lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-bold text-sm text-white">Recent Tickets</h3>
            <button className="text-xs text-[var(--color-accent)] hover:underline" onClick={() => navigate('/infra/admin/tickets')}>View all →</button>
          </div>
          <DataTable
            loading={loading}
            data={recentTickets}
            emptyText="No recent tickets"
            emptyIcon="🎫"
            columns={[
              { key: 'ticket_number', label: 'Ticket', render: r => <span className="font-mono text-xs text-[var(--color-accent)]">#{r.ticket_number}</span> },
              { key: 'subject', label: 'Subject', render: r => <span className="text-xs text-white truncate max-w-[180px] block">{r.subject}</span> },
              { key: 'status', label: 'Status', render: r => <StatusBadge status={r.status} /> },
              { key: 'priority', label: 'Priority', render: r => <StatusBadge status={r.priority} label={r.priority} /> },
            ]}
            onRowClick={r => navigate(`/infra/admin/tickets/${r.id}`)}
          />
        </Card>
      </div>

      {/* Recent Maintenance Runs */}
      <Card className="mt-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-bold text-sm text-white">Recent Maintenance Runs</h3>
          <button className="text-xs text-[var(--color-accent)] hover:underline" onClick={() => navigate('/infra/admin/maintenance')}>View all →</button>
        </div>
        <DataTable
          loading={loading}
          data={recentRuns}
          emptyText="No maintenance runs yet"
          emptyIcon="🔧"
          columns={[
            { key: 'environment',   label: 'Environment', render: r => <span className="text-xs text-white">{r.environment?.name}</span> },
            { key: 'run_type',      label: 'Type',        render: r => <span className="font-mono text-xs text-[var(--text-dim)]">{r.run_type}</span> },
            { key: 'status',        label: 'Status',      render: r => <StatusBadge status={r.status} /> },
            { key: 'passed_tasks',  label: 'Passed',      render: r => <span className="text-xs text-emerald-400">{r.passed_tasks ?? 0}</span> },
            { key: 'failed_tasks',  label: 'Failed',      render: r => <span className="text-xs text-red-400">{r.failed_tasks ?? 0}</span> },
            { key: 'started_at',    label: 'Started',     render: r => <span className="text-xs text-[var(--text-dim)]">{r.started_at ? new Date(r.started_at).toLocaleString() : '—'}</span> },
          ]}
        />
      </Card>
    </div>
  )
}
